import { toast } from "@/v2-ui/toast"
import ActionMenu, {
  type ActionMenuItemProps,
} from "@/v2-backoffice-client/components/actionMenu"
import { useFeatureGroupDeleteMutation } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.delete.mutation"
import { evictFeatureGroupQueries } from "@/v2-backoffice-client/license/license.utils"
import type { FeatureGroupFragment } from "@/v2-backoffice-client/license/featureGroup/__types__/license.featureGroup.fragment"

export type LicenseFeatureGroupDeleteMenuItemProps = Omit<
  ActionMenuItemProps,
  "onClick"
> & {
  featureGroup: FeatureGroupFragment
}

// Deletes a package. The API refuses while any license or live add-on carries
// it — a carried package is part of what customers are entitled to, so the
// two-step (detach everywhere, then delete) is deliberate. The confirm copy
// warns ahead when the counts say it will be refused.
function LicenseFeatureGroupDeleteMenuItem(
  props: LicenseFeatureGroupDeleteMenuItemProps,
) {
  const { featureGroup, ...menuItemProps } = props

  const isCarried = featureGroup.PlanCount > 0 || featureGroup.AddOnCount > 0

  const [deleteGroup, { loading, error }] = useFeatureGroupDeleteMutation({
    update: evictFeatureGroupQueries,
    onCompleted() {
      toast({
        variant: "success",
        title: "Package deleted",
        description: `${featureGroup.Name} was removed from the catalog.`,
      })
    },
  })

  return (
    <ActionMenu.ConfirmItem
      iconSrc="trash"
      variant="destructive"
      isLoading={loading}
      error={error}
      errorTitle="Failed to delete package"
      confirmTitle="Delete Package"
      confirmDescription={
        isCarried
          ? `${featureGroup.Name} is carried by ${featureGroup.PlanCount} license(s) and ${featureGroup.AddOnCount} add-on(s), so the delete will be refused — detach it from all of them first.`
          : `Are you sure you want to delete ${featureGroup.Name}? Nothing carries it, so nothing changes for any customer.`
      }
      confirmText="Delete"
      confirmVariant="destructive"
      data-feature-code="UI_BackofficeFeatureGroup_Row_Delete"
      onConfirm={async () => {
        await deleteGroup({ variables: { id: featureGroup.Id } })
      }}
      {...menuItemProps}
    >
      Delete
    </ActionMenu.ConfirmItem>
  )
}

export default LicenseFeatureGroupDeleteMenuItem
